import React from 'react'
import { HelmetFunc } from 'components/PageMetaData'
import { useTranslation } from 'react-i18next'
import { Layout } from 'layout/Layout'
import { DefaultFormBlock } from 'blocks/DefaultFormBlock'
import styled from 'styled-components'
import { backgroundColors, colors } from 'styles/colors'
import { mobileAfterBorder } from 'styles/mobileAfterBorder'
import { displayWidth } from 'styles/width'
import { Container } from 'components/Container'
import { graphql } from 'gatsby'
import { getDataByLanguage } from 'utils/getDataByLanguage'
import { LeftSidebar, RightSidebar } from 'components/SideBar'
import { TitleH1 } from 'components/TitleComponent'
import { ExampleOfProjectGenPlan } from 'blocks/ExampleOfProjectGenPlan'
import { indent } from 'styles/indent'
import { headerBg } from 'styles/headerBg'

const pageMetadata = {
    uk: {
        title:
            'Зразок генерального плану ділянки - схема, креслення та загальний вигляд | ClearLine',
        description:
            'Готовий приклад генерального плану ділянки: загальний вигляд території, схема генплану, повний комплект робочих креслень, 3D вигляд. Зразок правильного зонування та благоустрою від Clearline.',
    },
    ru: {
        title:
            'Образец генерального плана участка - схема, чертежи и общий вид | ClearLine',
        description:
            'Готовый пример генерального плана участка: общий вид территории, схема генплана, полный комплект рабочих чертежей, 3D вид. Образец правильного зонирования и благоустройства от Clearline.',
    },
    en: {
        title:
            'Sample general site plan - scheme, drawings and general view | ClearLine',
        description:
            'Ready example of a general site plan: general view of the territory, general plan scheme, full set of working drawings, 3D view. Sample of correct zoning and landscaping from Clearline.',
    },
}

const PageH1Wrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.contact};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
    :before {
        ${headerBg}
    }
`

const PageH1 = styled(TitleH1)`
    width: 100%;
    text-align: center;
    margin: 40px ${indent.mobile};
    @media (min-width: ${displayWidth.tablet}) {
        margin: 56px ${indent.heroColumnTablet};
        text-align: left;
    }
    @media (min-width: ${displayWidth.desktop}) {
        margin: 60px ${indent.heroColumnDesktop};
    }
`

const CompositionWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.project};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
`

const CompositionTitle = styled.h3`
    font-family: 'Yeseva One', sans-serif;
    font-size: 22px;
    line-height: 28px;
    letter-spacing: 1px;
    margin: 16px auto;
    color: ${colors.dark};
`

const CompositionList = styled.ul`
    margin: 16px;
    list-style: none;
    columns: 1;
    @media (min-width: ${displayWidth.tablet}) {
        columns: 2;
    }
    @media (min-width: ${displayWidth.desktop}) {
        columns: 3;
    }
`

const CompositionItem = styled.li`
    font-size: 16px;
    line-height: 26px;
    letter-spacing: 0.4px;
    color: ${colors.dark};
    padding: 4px 0;
    break-inside: avoid;
    &:before {
        content: '— ';
        color: ${colors.accentText};
    }
`

const GenplanPage = ({
    data,
}: {
    data: {
        allGenplanImagesDataYaml: {
            edges: {
                node: {
                    title: string
                    description: string
                    compositionTitle: string
                    items: { title: string; alt: string }[]
                    parent: { name: string }
                }
            }[]
        }
    }
}) => {
    const { i18n } = useTranslation()

    const genplanData = getDataByLanguage(
        data.allGenplanImagesDataYaml,
        i18n.language
    )

    const h1Titles: Record<string, string> = {
        ru: 'Генеральный план участка с комплектом рабочих чертежей',
        uk: 'Генеральний план ділянки з комплектом робочих креслень',
        en: 'General site plan with a set of working drawings',
    }

    const lang = i18n.language
    const compositionItems: string[] = (genplanData.items ?? []).map(
        (item: { title: string }) => item.title
    )

    return (
        <Layout>
            <HelmetFunc data={pageMetadata} />
            <PageH1Wrapper>
                <LeftSidebar background={backgroundColors.contact} />
                <Container columns={'1fr'} tabletColumns={'1fr'}>
                    <PageH1>{h1Titles[lang] ?? h1Titles.ru}</PageH1>
                </Container>
                <RightSidebar />
            </PageH1Wrapper>
            <ExampleOfProjectGenPlan withH2Header />
            <CompositionWrapper>
                <LeftSidebar background={backgroundColors.project} />
                <Container columns={'1fr'} tabletColumns={'1fr'}>
                    <CompositionTitle>
                        {genplanData.compositionTitle}
                    </CompositionTitle>
                    <CompositionList>
                        {compositionItems.map((item, index) => (
                            <CompositionItem key={index}>
                                {item}
                            </CompositionItem>
                        ))}
                    </CompositionList>
                </Container>
                <RightSidebar background={backgroundColors.project} />
            </CompositionWrapper>
            <DefaultFormBlock
                withPhoneMobile
                tracking={{
                    conversionType: 'FormGenplan',
                    eventCategory: 'FormGenplan',
                }}
            />
        </Layout>
    )
}

export default GenplanPage

export const query = graphql`
    query {
        allGenplanImagesDataYaml {
            edges {
                node {
                    title
                    description
                    compositionTitle
                    items {
                        title
                        alt
                    }
                    parent {
                        ... on File {
                            name
                        }
                    }
                }
            }
        }
    }
`
